import * as THREE from 'three';

const GLINT_LIFE = 0.22;
const MIN_RANGE = 22;
const PLAYER_GLINT_SCOPE_S = 3;

type Glint = {
  sprite: THREE.Sprite;
  life: number;
};

/** Brief bot-shot flashes + persistent player scope glint visible to bots. */
export class FpsSniperGlint {
  private readonly glints: Glint[] = [];
  private readonly tex: THREE.CanvasTexture;
  private readonly playerGlint: THREE.Sprite;
  private playerScopeT = 0;
  private playerGlintActive = false;

  constructor(private readonly scene: THREE.Scene) {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d')!;
    const grad = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.35, 'rgba(255,240,180,0.85)');
    grad.addColorStop(1, 'rgba(255,200,80,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 32, 32);
    this.tex = new THREE.CanvasTexture(canvas);
    this.tex.colorSpace = THREE.SRGBColorSpace;

    const playerMat = new THREE.SpriteMaterial({
      map: this.tex,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.playerGlint = new THREE.Sprite(playerMat);
    this.playerGlint.scale.set(0.42, 0.42, 1);
    this.playerGlint.visible = false;
    this.scene.add(this.playerGlint);
  }

  /** Track ADS scoped time; glint appears after 3s. */
  setPlayerScoped(scoped: boolean, dt: number, worldPos: THREE.Vector3): void {
    if (scoped) {
      this.playerScopeT += dt;
    } else {
      this.playerScopeT = 0;
    }
    this.playerGlintActive = scoped && this.playerScopeT >= PLAYER_GLINT_SCOPE_S;
    this.playerGlint.visible = this.playerGlintActive;
    if (this.playerGlintActive) {
      this.playerGlint.position.copy(worldPos);
      this.playerGlint.position.y += 1.52;
      const pulse = 1 + Math.sin(performance.now() * 0.009) * 0.22;
      const mat = this.playerGlint.material as THREE.SpriteMaterial;
      mat.opacity = 0.55 + Math.sin(performance.now() * 0.012) * 0.25;
      this.playerGlint.scale.set(0.38 * pulse, 0.38 * pulse, 1);
    } else {
      const mat = this.playerGlint.material as THREE.SpriteMaterial;
      mat.opacity = 0;
    }
  }

  isPlayerGlintActive(): boolean {
    return this.playerGlintActive;
  }

  /** Spawn glint if shot origin is far enough from the player. */
  maybeFromBotShot(origin: THREE.Vector3, playerPos: THREE.Vector3, isSniper: boolean): void {
    const dist = origin.distanceTo(playerPos);
    if (dist < MIN_RANGE && !isSniper) return;
    if (isSniper && dist < 14) return;

    const mat = new THREE.SpriteMaterial({
      map: this.tex,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const sprite = new THREE.Sprite(mat);
    sprite.position.copy(origin);
    sprite.position.y += 0.15;
    const scale = isSniper ? 0.55 : 0.38;
    sprite.scale.set(scale, scale, 1);
    this.scene.add(sprite);
    this.glints.push({ sprite, life: GLINT_LIFE });
  }

  update(dt: number): void {
    for (let i = this.glints.length - 1; i >= 0; i--) {
      const g = this.glints[i]!;
      g.life -= dt;
      const u = 1 - Math.max(0, g.life / GLINT_LIFE);
      const mat = g.sprite.material as THREE.SpriteMaterial;
      mat.opacity = 0.95 * (1 - u);
      g.sprite.scale.multiplyScalar(1 + dt * 2.2);
      if (g.life <= 0) {
        g.sprite.removeFromParent();
        mat.dispose();
        this.glints.splice(i, 1);
      }
    }
  }

  dispose(): void {
    for (const g of this.glints) {
      g.sprite.removeFromParent();
      (g.sprite.material as THREE.SpriteMaterial).dispose();
    }
    this.glints.length = 0;
    this.playerGlint.removeFromParent();
    (this.playerGlint.material as THREE.SpriteMaterial).dispose();
    this.tex.dispose();
  }
}
